import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buttonClasses } from 'components/common/tailwindClassnames';

// Bidirectional / account-reuse (GUNDI-5595): action configurations are stored on
// the INTEGRATION record, not on the Route, so every Route using this account
// inherits changes made here. Field mappings are NOT affected — those live on the
// Route and stay per-flow.
type SharedAccountNoticeProps = {
  accountName: string;
  // name is absent when the user can't resolve the Route (e.g. it lives in
  // a workspace they can't see) — those render as a count, never as ids/links.
  usedBy: { id: string; name?: string }[];
};

const SharedAccountNotice = ({
  accountName,
  usedBy,
}: SharedAccountNoticeProps) => {
  const { t } = useTranslation();
  if (usedBy.length === 0) return null;
  const named = usedBy.filter((c) => c.name);
  const hiddenCount = usedBy.length - named.length;
  return (
    // Border only, no fill — a full yellow wash overstates an FYI.
    <div className="mb-5 rounded border border-unlisted-orange-200 px-4 py-3">
      <div className="text-sm text-ui-text-primary-text">
        <span className="font-semibold">{t('Shared account.')}</span>{' '}
        {usedBy.length === 1
          ? t('{{name}} is also used by another Route.', { name: accountName })
          : t('{{name}} is also used by {{count}} other Routes.', {
              name: accountName,
              count: usedBy.length,
            })}{' '}
        {t(
          'Credentials and action settings below are stored on the account, so changes apply everywhere it is used. Field mappings are per Route and are not affected.',
        )}
      </div>
      {(named.length > 0 || hiddenCount > 0) && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {named.map((c) => (
            <Link
              key={c.id}
              to={`/routes/${c.id}`}
              className={buttonClasses.inlineLink}
            >
              {c.name} ▸
            </Link>
          ))}
          {hiddenCount > 0 && (
            <span className="text-sm text-ui-text-primary-text">
              {hiddenCount === 1
                ? t("{{count}} Route in a workspace you can't view", {
                    count: hiddenCount,
                  })
                : t("{{count}} Routes in workspaces you can't view", {
                    count: hiddenCount,
                  })}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SharedAccountNotice;
